import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LangProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Repeating bento pattern of column spans + aspect ratios (desktop)
const PATTERN = [
  "md:col-span-8 aspect-[4/3]",
  "md:col-span-4 aspect-[3/4]",
  "md:col-span-4 aspect-square",
  "md:col-span-4 aspect-[4/3]",
  "md:col-span-4 aspect-[3/4]",
  "md:col-span-6 aspect-[16/10]",
  "md:col-span-6 aspect-[16/10]",
];

const Gallery = () => {
  const { t } = useLang();
  const [images, setImages] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        if (!cancelled) setImages(res.data);
      } catch (err) {
        console.error("Failed to load gallery", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (images.length === 0) return null;

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-obsidian py-24 md:py-32 grain"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="divider-line" />
              <span className="overline">{t.gallery.overline}</span>
            </div>
            <h2 className="font-serif font-light text-ivory text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              {t.gallery.title}
            </h2>
          </div>
          <p className="max-w-md text-ivory/60 text-base sm:text-lg leading-relaxed">
            {t.gallery.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          {images.map((img, i) => (
            <motion.div
              key={img.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.08 }}
              data-testid={`gallery-cell-${i}`}
              className={`relative overflow-hidden bg-surface ${PATTERN[i % PATTERN.length]}`}
            >
              <img
                src={img.src}
                alt={img.alt || `Grizzly ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover hover:scale-[1.06] transition-transform duration-[1200ms]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/40 via-transparent to-transparent pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Gallery;
